<app-card headerText="Audit Log" size="sm" icon="History" [collapsible]="true" [(collapsed)]="collapsed">
  <div card-summary class="flex items-center justify-between gap-2">
    <app-badge [title]="auditEntries().length + (auditEntries().length === 1 ? ' event' : ' events')" variant="secondary" size="xs"></app-badge>
    <app-text size="xs" color="muted">
      {{ auditEntries().length > 0 ? 'Click to view logs' : 'No events yet' }}
    </app-text>
  </div>
  
  @if (auditEntries().length > 0) {
    <!-- Filters -->
    <div class="flex gap-1.5 mb-5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide flex-wrap">
      <app-button size="xs" [variant]="filterType() === 'ALL' ? 'primary' : 'ghost'" (click)="filterType.set('ALL')">
        All ({{ auditEntries().length }})
      </app-button>
      <app-button size="xs" [variant]="filterType() === 'STATUS_CHANGE' ? 'primary' : 'ghost'" (click)="filterType.set('STATUS_CHANGE')">
        Status
      </app-button>
      <app-button size="xs" [variant]="filterType() === 'STOP_STATUS_CHANGE' ? 'primary' : 'ghost'" (click)="filterType.set('STOP_STATUS_CHANGE')">
        Stops
      </app-button>
      <app-button size="xs" [variant]="filterType() === 'ASSIGNMENT_CHANGE' ? 'primary' : 'ghost'" (click)="filterType.set('ASSIGNMENT_CHANGE')">
        Assignments
      </app-button>
      <app-button size="xs" [variant]="filterType() === 'OVERRIDE' ? 'primary' : 'ghost'" (click)="filterType.set('OVERRIDE')">
        Overrides
      </app-button>
    </div>

    <!-- Timeline Container -->
    <div class="relative pl-7 space-y-3.5">
      <!-- Continuous vertical timeline line -->
      <div class="absolute left-3.5 top-3 bottom-3 w-0.5 bg-border/70"></div>

      @for (entry of filteredEntries(); track entry.id || $index) {
        @let node = getNodeConfig(entry.eventType);
        @let user = resolveUser(entry);

        <div class="relative flex items-start gap-3 group" animate.enter="hv-item-enter" [style.--stagger-idx]="$index">
          <!-- Timeline node icon -->
          <div class="absolute -left-7 top-1 flex items-center justify-center w-7 h-7 rounded-full border shadow-xs z-10"
               [ngClass]="[node.bgClass, node.borderClass, node.textClass]">
            <lucide-icon [name]="node.icon" size="14"></lucide-icon>
          </div>

          <!-- Event Card -->
          <div class="flex-1 min-w-0 bg-surface border border-border/80 rounded-xl p-3 shadow-xs transition-all hover:border-primary/30">
            <!-- Header: Concise Single-Row Title & Time -->
            <div class="flex items-center justify-between gap-3">
              <div class="font-semibold text-sm text-foreground truncate">
                {{ formatActionTitle(entry) }}
              </div>
              <div class="flex items-center gap-1 text-xs text-muted shrink-0" [title]="(toAuditDate(entry.performedAt) | date:'medium') || ''">
                <lucide-icon name="Clock" size="11" class="text-muted"></lucide-icon>
                <span>{{ entry.performedAt | timeAgo }}</span>
              </div>
            </div>

            <!-- Transition / Diff Details -->
            @if (entry.eventType === 'STATUS_CHANGE' && entry.previousValue && entry.newValue) {
              <div class="flex items-center gap-2 mt-2 pt-2 border-t border-border/40 flex-wrap">
                <span class="text-xs text-muted font-medium">Status:</span>
                <app-badge [title]="'' + entry.previousValue" [variant]="getStatusBadgeVariant(entry.previousValue)" size="xs" badgeStyle="soft"></app-badge>
                <lucide-icon name="ArrowRight" size="12" class="text-muted shrink-0"></lucide-icon>
                <app-badge [title]="'' + entry.newValue" [variant]="getStatusBadgeVariant(entry.newValue)" size="xs"></app-badge>
              </div>
            } @else if (hasFieldDiff(entry)) {
              <div class="flex items-center gap-2 mt-2 pt-2 border-t border-border/40 text-xs text-muted flex-wrap">
                <span class="font-medium text-foreground">{{ formatField(entry.field) }}:</span>
                <span class="line-through text-muted">{{ formatFieldValue(entry.field, entry.previousValue) }}</span>
                <lucide-icon name="ArrowRight" size="12" class="text-muted shrink-0"></lucide-icon>
                <span class="font-medium text-foreground">{{ formatFieldValue(entry.field, entry.newValue) }}</span>
              </div>
            } @else if (entry.action) {
              <div class="mt-2 pt-2 border-t border-border/40 text-xs text-muted">
                {{ entry.action }}
              </div>
            }

            <!-- Reason Callout (Only if meaningful and not boilerplate) -->
            @if (entry.reason && isMeaningfulReason(entry.reason)) {
              <div class="mt-2 flex items-start gap-2 p-2 rounded-lg text-xs"
                   [ngClass]="entry.reasonRequired ? 'bg-warning/10 border border-warning/20 text-warning' : 'bg-surface-high border border-border/60 text-muted'">
                <lucide-icon name="MessageSquare" size="13" class="shrink-0 mt-0.5"
                             [ngClass]="entry.reasonRequired ? 'text-warning' : 'text-muted'"></lucide-icon>
                <span class="italic text-foreground">"{{ entry.reason }}"</span>
              </div>
            }

            <!-- User Attribution Footer -->
            <div class="flex items-center justify-between gap-2 mt-2.5 pt-2 border-t border-border/40 flex-wrap text-xs">
              <div class="flex items-center gap-2 min-w-0">
                <div class="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0">
                  {{ user.initials }}
                </div>
                <span class="font-medium text-foreground truncate">
                  {{ user.name }}
                </span>
                <app-badge [variant]="user.roleVariant" size="xs" [title]="user.role"></app-badge>
              </div>
              <div class="flex items-center gap-1 text-[11px] text-muted">
                <lucide-icon [name]="user.viaIcon" size="12" class="text-muted shrink-0"></lucide-icon>
                <span>{{ user.viaLabel }}</span>
              </div>
            </div>
          </div>
        </div>
      }

      @if (filteredEntries().length === 0) {
        <div class="text-center py-6 bg-surface rounded-xl border border-border">
          <app-text color="muted" size="sm">No events match the selected filter.</app-text>
        </div>
      }
    </div>
  } @else {
    <div class="text-center py-8">
      <lucide-icon name="History" class="w-8 h-8 text-muted mx-auto mb-2 opacity-40"></lucide-icon>
      <app-text color="muted" size="sm">No audit events recorded yet.</app-text>
    </div>
  }
</app-card>
